import { onDocumentReady } from '../utils/events';
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';

const SLIDER_OPTIONS = {
  type: 'slide',
  perPage: 4,
  perMove: 1,
  drag: true,
  flickPower: 150,
  dragMinThreshold: {
    mouse: 40,
    touch: 70,
  },
  gap: 40,
  arrows: true,
  pagination: false,
  keyboard: 'global',
  rewind: false,
  trimSpace: true,
  focus: 0,
  omitEnd: true,

  breakpoints: {
    1200: {
      perPage: 3,
      gap: 30,
    },
    1023: {
      perPage: 2,
      gap: 25,
    },
    640: {
      perPage: 1,
      gap: 20,
      padding: { right: '15%' },
    },
  },
};

/**
 * Minimal YouTube lightbox. The iframe is only created on open and torn down
 * on close, so nothing is requested — or left playing — otherwise.
 */
const lightbox = (() => {
  let root = null;
  let frame = null;
  let lastFocused = null;

  const close = () => {
    if (!root || !root.classList.contains('is-open')) {
      return;
    }

    root.classList.remove('is-open');
    document.body.classList.remove('video-modal-open');
    frame.innerHTML = '';

    if (lastFocused) {
      lastFocused.focus();
      lastFocused = null;
    }
  };

  const build = () => {
    root = document.createElement('div');
    root.className = 'video-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = `
      <button type="button" class="video-modal__backdrop" tabindex="-1" aria-hidden="true"></button>
      <div class="video-modal__inner">
        <button type="button" class="video-modal__close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
            <path d="m1 1 12 12M13 1 1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="video-modal__frame"></div>
      </div>`;

    document.body.appendChild(root);
    frame = root.querySelector('.video-modal__frame');

    root
      .querySelector('.video-modal__backdrop')
      .addEventListener('click', close);
    root.querySelector('.video-modal__close').addEventListener('click', close);

    document.addEventListener('keydown', (event) => {
      if ('Escape' === event.key) {
        close();
      }
    });
  };

  const open = (videoId, ratio, label, closeLabel) => {
    if (!root) {
      build();
    }

    lastFocused = document.activeElement;

    const closeButton = root.querySelector('.video-modal__close');
    closeButton.setAttribute('aria-label', closeLabel || 'Close');
    root.setAttribute('aria-label', label || '');
    root.style.setProperty(
      '--video-modal-ratio',
      (ratio || '9/16').replace('/', ' / ')
    );

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
      videoId
    )}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    iframe.setAttribute('title', label || 'YouTube video player');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('allowfullscreen', '');

    frame.appendChild(iframe);
    root.classList.add('is-open');
    document.body.classList.add('video-modal-open');
    closeButton.focus();
  };

  return { open, close };
})();

onDocumentReady(() => {
  document.querySelectorAll('.videos').forEach((section) => {
    const sliderEl = section.querySelector('.videos__slider');
    const listEl = section.querySelector('.videos__items');
    const arrowsEl = section.querySelector('.videos__arrows');

    if (!sliderEl || !listEl) {
      return;
    }

    const splide = new Splide(sliderEl, SLIDER_OPTIONS);

    /**
     * Arrows are pointless when everything already fits on one page.
     */
    const syncArrows = () => {
      if (!arrowsEl) {
        return;
      }

      const { perPage } = splide.options;
      arrowsEl.classList.toggle('is-hidden', splide.length <= perPage);
    };

    splide.on('mounted refresh resized', syncArrows);
    splide.mount();

    // Title strip: one entry per video, kept in step with the slider.
    const navEl = section.querySelector('.videos__nav');
    const navItems = Array.from(section.querySelectorAll('.videos__nav-item'));

    if (!navEl || !navItems.length) {
      return;
    }

    /**
     * Mark one entry active and scroll the strip so it lines up with the
     * leading card, the way a thumbnail selector would.
     */
    const setActive = (index) => {
      let active = null;

      navItems.forEach((item) => {
        const isActive = Number(item.dataset.index) === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');

        if (isActive) {
          active = item;
        }
      });

      if (!active) {
        return;
      }

      // Only scroll the strip itself — never the page.
      const target = active.offsetLeft - navEl.offsetLeft;
      const max = navEl.scrollWidth - navEl.clientWidth;

      navEl.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: 'smooth',
      });
    };

    // Splide clamps to the last page, so the slide the user picked may not end
    // up first. Remember the intent so the highlight follows the click rather
    // than the clamped landing index.
    let pickedIndex = null;

    navItems.forEach((item) => {
      item.addEventListener('click', () => {
        pickedIndex = Number(item.dataset.index);
        splide.go(pickedIndex);
        setActive(pickedIndex);
      });
    });

    // Dragging or the arrows should move the highlight too.
    splide.on('moved', (newIndex) => {
      setActive(null === pickedIndex ? newIndex : pickedIndex);
      pickedIndex = null;
    });

    setActive(splide.index);
  });

  document.addEventListener('click', (event) => {
    const card = event.target.closest && event.target.closest('.videos__card');

    if (!card) {
      return;
    }

    const section = card.closest('.videos');

    lightbox.open(
      card.dataset.videoId,
      card.dataset.videoRatio,
      card.getAttribute('aria-label'),
      section && section.dataset.closeLabel
    );
  });
});
